import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Input, DatePicker, Select, Button, message, ConfigProvider } from 'antd';
import { CarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { post, get } from '../../../../services/ApiService';

const THEME = {
  primary: '#4e7661',
  light: '#eef4f1',
  border: '#c5d9ce',
};

const CARRIER_OPTIONS = [
  { value: 'RM', label: 'Royal Mail' },
  { value: 'AMZ', label: 'Amazon Shipping' },
];

const getInitialValues = (order) => ({
  trackingNumber: order?.trackingNumber,
  shippingDate: order?.shippingDate ? dayjs(order.shippingDate) : dayjs(),
  carrier: CARRIER_OPTIONS.some(({ value }) => value === order?.carrier) ? order.carrier : undefined,
  postageCode: order?.postageCode,
});

const AddTrackingModal = ({ open, onCancel, onSuccess, order }) => {
  const [form] = Form.useForm();
  const carrier = Form.useWatch('carrier', form);
  const [postageCodes, setPostageCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !carrier) {
      setPostageCodes([]);
      return;
    }
    setCodesLoading(true);
    get(`api/orders/postage-codes?carrier=${carrier}`)
      .then((res) => {
        if (res.success) setPostageCodes(res.postageCodes);
        else message.error(res.error || 'Failed to load postage codes');
      })
      .catch(() => message.error('Failed to load postage codes'))
      .finally(() => setCodesLoading(false));
  }, [open, carrier]);

  const handleCarrierChange = () => form.setFieldValue('postageCode', undefined);

  const handleClose = () => {
    if (submitting) return;
    onCancel();
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const res = await post(`api/orders/tracking/${order._id}`, {
        ...values,
        shippingDate: values.shippingDate.format('YYYY-MM-DD'),
      });
      if (res.success) {
        message.success(res.message);
        onSuccess(res.data);
      } else {
        message.error(res.error || 'Failed to add tracking');
      }
    } catch (error) {
      message.error('Failed to add tracking');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: THEME.primary,
          colorPrimaryHover: '#3f6250',
          colorBorder: THEME.border,
          borderRadius: 8,
          controlHeightLG: 44,
          fontSize: 13,
        },
      }}
    >
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: THEME.light,
              border: `1px solid ${THEME.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CarOutlined style={{ color: THEME.primary, fontSize: 16 }} />
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
              Add Tracking
            </div>
            <div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>
              Save the courier tracking details on this order
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={480}
      maskClosable={false}
      closable={false}
      destroyOnClose
    >
      <div style={{ padding: '4px 0 8px' }}>
        <Form className="add-tracking-form" form={form} layout="vertical" initialValues={getInitialValues(order)} onFinish={handleSubmit}>
          <Form.Item
            name="trackingNumber"
            label="Tracking Number"
            rules={[{ required: true, whitespace: true, message: 'Tracking number is required' }]}
          >
            <Input size="large" placeholder="Enter tracking number" />
          </Form.Item>
          <Form.Item
            name="shippingDate"
            label="Shipping Date"
            rules={[{ required: true, message: 'Shipping date is required' }]}
          >
            <DatePicker size="large" style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            name="carrier"
            label="Carrier"
            rules={[{ required: true, message: 'Carrier is required' }]}
          >
            <Select size="large" placeholder="Select carrier" options={CARRIER_OPTIONS} onChange={handleCarrierChange} />
          </Form.Item>
          <Form.Item
            name="postageCode"
            label="Postage Code"
            rules={[{ required: true, message: 'Postage code is required' }]}
            style={{ marginBottom: 16 }}
          >
            <Select
              size="large"
              showSearch
              optionFilterProp="label"
              placeholder={carrier ? 'Select postage code' : 'Select a carrier first'}
              disabled={!carrier}
              loading={codesLoading}
              options={postageCodes.map(({ code, desc }) => ({
                value: code,
                label: desc ? `${code} - ${desc}` : code,
              }))}
            />
          </Form.Item>
        </Form>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            block
            size="large"
            disabled={submitting}
            onClick={handleClose}
            style={{ height: 44, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            block
            size="large"
            icon={<CarOutlined />}
            loading={submitting}
            onClick={form.submit}
            style={{ background: THEME.primary, borderColor: THEME.primary, fontWeight: 600, height: 44 }}
          >
            Add tracking
          </Button>
        </div>
      </div>
    </Modal>
    </ConfigProvider>
  );
};

AddTrackingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  order: PropTypes.object,
};

export default AddTrackingModal;
